<template>
    <div class="mobile-form-container">
        <branding-header />

        <h3 style="color: var(--v-primary-base); font-size: 1.25rem; margin-top: 0.75rem; margin-bottom: 0.25rem; text-align: center;">Confirm Your Information</h3>
        <p style="text-align: center; margin-bottom: 1.5rem;">
            Please review and verify the details below
        </p>

        <form @submit.prevent="submitConfirmation">
            <rc-base-input
                v-model="form_data.first_name"
                label="First Name"
                outlined
                required
                @validated="valid => required_field_validity.first_name = valid"
            />

            <rc-base-input
                v-model="form_data.last_name"
                label="Last Name"
                outlined
                required
                @validated="valid => required_field_validity.last_name = valid"
            />

            <rc-base-input
                v-model="form_data.email"
                label="Email"
                type="email"
                outlined
                required
                @validated="valid => required_field_validity.email = valid"
            />

            <rc-base-input
                v-model="form_data.phone"
                label="Phone"
                type="tel"
                outlined
                required
                @validated="valid => required_field_validity.phone = valid"
            />

            <rc-base-input
                v-model="form_data.birth_date"
                label="Birthdate"
                type="birthday"
                required
                outlined
                @validated="valid => required_field_validity.birth_date = valid"
            />

            <div style="display: grid; grid-template-columns: 1fr auto; gap: 0.5rem;">
                <rc-base-input
                    v-model="form_data.address.city"
                    label="City"
                    outlined
                    required
                    @validated="valid => required_field_validity.city = valid"
                />
                <rc-base-input
                    v-model="form_data.address.state"
                    label="State"
                    style="max-width: 80px;"
                    outlined
                    required
                    @validated="valid => required_field_validity.state = valid"
                />
            </div>

            <rc-base-input
                v-model="form_data.address.zip"
                label="ZIP Code"
                outlined
                required
                @validated="valid => required_field_validity.zip = valid"
            />

            <v-btn
                style="width: 100%; background-color: var(--brand-secondary); color: var(--brand-primary); margin-top: 0.25rem; margin-bottom: 1rem;"
                text
                small
                @click="checkAll"
            >
                <v-icon
                    aria-hidden="true"
                    left
                    small
                >
                    mdi-check-all
                </v-icon>
                Check All
            </v-btn>

            <div style="display: flex; flex-direction: column; gap: .25rem; margin-bottom: 1.5rem;">
                <v-checkbox
                    v-model="confirmations.first_name_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>First name</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.last_name_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Last name</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.email_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Email</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.phone_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Phone</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.birth_date_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Birthdate</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.address_correct"
                    class="mobile-checkbox"
                    color="primary"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>City, State, and Zip</strong> are correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.text_consent"
                    class="mobile-checkbox consent-checkbox"
                    color="primary"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label consent-label">
                            I give <strong>Barrel Creek Vineyards</strong> permission to send text messages
                            with info, promotions and offers to the number above, including use of an automated
                            system. Consent is not a condition of purchase. Texts are recorded. Message and/or data
                            rates may apply. Use is subject to <a
                                href="#"
                                @click.prevent.stop="showTerms"
                            >Terms</a> with an arbitration clause and a <a
                                href="#"
                                @click.prevent.stop="showPrivacy"
                            >Privacy Policy</a>.
                        </span>
                    </template>
                </v-checkbox>
            </div>

            <v-btn
                style="margin: .5rem auto; background-color: var(--brand-primary); color: white; display: block; width: 100%;"
                :disabled="!canSubmit"
                type="submit"
            >
                Confirm Information
            </v-btn>
        </form>

        <placeholder-dialog ref="placeholderDialog" />
    </div>
</template>

<script>
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';

export default {
    name: 'AccountConfirmationForm',

    components: {
        BrandingHeader,
        PlaceholderDialog
    },

    props: {
        customer_data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            required_field_validity: {
                first_name: false,
                last_name: false,
                email: false,
                phone: false,
                birth_date: false,
                city: false,
                state: false,
                zip: false
            },
            form_data: {
                first_name: this.customer_data.first_name || '',
                last_name: this.customer_data.last_name || '',
                email: this.customer_data.email || '',
                phone: this.customer_data.phone || '',
                birth_date: this.customer_data.birth_date || '',
                address: {
                    city: this.customer_data.address?.city || '',
                    state: this.customer_data.address?.state || '',
                    zip: this.customer_data.address?.zip || ''
                },
            },
            original_data: null,
            confirmations: {
                first_name_correct: false,
                last_name_correct: false,
                email_correct: false,
                phone_correct: false,
                birth_date_correct: false,
                address_correct: false,
                text_consent: false
            },
        }
    },

    computed: {
        form_valid() {
            return Object.values(this.required_field_validity).every(valid => valid);
        },

        canSubmit() {
            if (!this.form_valid) return false;

            const required_confirmations = [
                'first_name_correct',
                'last_name_correct',
                'email_correct',
                'phone_correct',
                'address_correct',
            ];

            return required_confirmations.every(key => this.confirmations[key]);
        },
    },

    methods: {
        checkAll() {
            this.confirmations = Object.entries(this.confirmations).reduce((acc, cur) => {
                const [key] = cur;
                acc[key] = true;
                return acc;
            }, {})
        },

        submitConfirmation() {
            if (this.canSubmit) {
                this.$emit('submitted', {
                    data: this.form_data,
                    confirmations: this.confirmations,
                    has_text_consent: this.confirmations.text_consent
                });
            }
        },

        showTerms() {
            this.$refs.placeholderDialog.show('Terms & Conditions');
        },

        showPrivacy() {
            this.$refs.placeholderDialog.show('Privacy Policy');
        }
    },

    mounted() {
        this.original_data = { ...this.form_data };
    }
}
</script>

<style lang="scss" scoped>
a {
    color: inherit;
}

.consent-label {
    padding-top: 4px;
}
</style>